/**
 * <ai-quantity-input> — 数量输入组件
 *
 * 职责：
 * - 渲染 +/- 按钮和数字输入框
 * - 管理 min/max/step 校验
 * - 边界自动 disable 按钮
 * - 派发 quantity:change 事件
 *
 * 用法：
 * <ai-quantity-input value="1" min="1" max="99999" step="1"></ai-quantity-input>
 *
 * DOM 结构（组件自渲染）：
 *
 * <ai-quantity-input>
 *   button.qi-btn[data-action="minus"]
 *   input.qi-input[type="number"]
 *   button.qi-btn[data-action="plus"]
 * </ai-quantity-input>
 *
 * 属性：
 * - value:  当前数量（默认 1）
 * - min:    最小值（默认 1）
 * - max:    最大值（默认 99999）
 * - step:   步长（默认 1）
 *
 * 事件：
 * - quantity:change  { quantity, min, max }
 */

if (!customElements.get("ai-quantity-input")) {
    customElements.define(
        "ai-quantity-input",
        class QuantityInput extends HTMLElement {
            connectedCallback() {
                this.style.display = "inline-flex";
                this.style.alignItems = "center";

                this._min = parseInt(this.getAttribute("min")) || 1;
                this._max = parseInt(this.getAttribute("max")) || 99999;
                this._step = parseInt(this.getAttribute("step")) || 1;
                this._value = parseInt(this.getAttribute("value")) || this._min;

                this._render();
                this._updateState();
            }

            static get observedAttributes() {
                return ["value", "min", "max", "step"];
            }

            attributeChangedCallback(name, oldValue, newValue) {
                if (oldValue === newValue) return;
                if (!this._input) return; // not yet rendered

                switch (name) {
                    case "value":
                        this._value = parseInt(newValue) || this._min;
                        this._input.value = this._value;
                        this._updateState();
                        break;
                    case "min":
                        this._min = parseInt(newValue) || 1;
                        this._input.min = this._min;
                        this._updateState();
                        break;
                    case "max":
                        this._max = parseInt(newValue) || 99999;
                        this._input.max = this._max;
                        this._updateState();
                        break;
                    case "step":
                        this._step = parseInt(newValue) || 1;
                        this._input.step = this._step;
                        break;
                }
            }

            _render() {
                // Minus button
                this._minusBtn = document.createElement("button");
                this._minusBtn.className = "qi-btn";
                this._minusBtn.type = "button";
                this._minusBtn.setAttribute("data-action", "minus");
                this._minusBtn.setAttribute("aria-label", "Decrease quantity");
                this._minusBtn.textContent = "\u2212"; // −
                this._minusBtn.addEventListener("click", () => this._stepDown());

                // Input
                this._input = document.createElement("input");
                this._input.className = "qi-input";
                this._input.type = "number";
                this._input.inputMode = "numeric";
                this._input.min = this._min;
                this._input.max = this._max;
                this._input.step = this._step;
                this._input.value = this._value;
                this._input.setAttribute("aria-label", "Quantity");
                this._input.addEventListener("change", () => this._onInputChange());
                this._input.addEventListener("blur", () => this._onInputChange());

                // Plus button
                this._plusBtn = document.createElement("button");
                this._plusBtn.className = "qi-btn";
                this._plusBtn.type = "button";
                this._plusBtn.setAttribute("data-action", "plus");
                this._plusBtn.setAttribute("aria-label", "Increase quantity");
                this._plusBtn.textContent = "+";
                this._plusBtn.addEventListener("click", () => this._stepUp());

                this.appendChild(this._minusBtn);
                this.appendChild(this._input);
                this.appendChild(this._plusBtn);
            }

            _stepUp() {
                const newValue = Math.min(this._value + this._step, this._max);
                if (newValue !== this._value) {
                    this._value = newValue;
                    this._input.value = this._value;
                    this._sync();
                }
            }

            _stepDown() {
                const newValue = Math.max(this._value - this._step, this._min);
                if (newValue !== this._value) {
                    this._value = newValue;
                    this._input.value = this._value;
                    this._sync();
                }
            }

            _onInputChange() {
                let val = parseInt(this._input.value);
                if (isNaN(val)) val = this._min;
                val = Math.max(this._min, Math.min(this._max, val));

                // Snap to step
                const remainder = (val - this._min) % this._step;
                if (remainder !== 0) {
                    val = val - remainder;
                }

                this._value = val;
                this._input.value = val;
                this._sync();
            }

            _sync() {
                this.setAttribute("value", String(this._value));
                this._updateState();
                this.dispatchEvent(
                    new CustomEvent("quantity:change", {
                        bubbles: true,
                        detail: {
                            quantity: this._value,
                            min: this._min,
                            max: this._max,
                        },
                    }),
                );
            }

            /** Disable buttons at boundaries */
            _updateState() {
                if (!this._minusBtn) return;
                this._minusBtn.disabled = this._value <= this._min;
                this._plusBtn.disabled = this._value >= this._max;

                if (this._minusBtn.disabled) {
                    this._minusBtn.setAttribute("data-disabled", "");
                } else {
                    this._minusBtn.removeAttribute("data-disabled");
                }

                if (this._plusBtn.disabled) {
                    this._plusBtn.setAttribute("data-disabled", "");
                } else {
                    this._plusBtn.removeAttribute("data-disabled");
                }
            }
        },
    );
}
